// src/components/DarkWebView.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import { Database, Search, Edit, Save, X, ExternalLink, GripVertical, Shield, Eye, EyeOff, Key, FileText, MessageSquare, AlertTriangle } from 'lucide-react';

const DarkWebView = ({ canSeeSuperAdminSettings, canEditIncidents, setShowAddDarkWebFinding }) => {
  const { getToken, isSignedIn } = useAuth();
  const { activeCompany } = useTenant();

  // State
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [editedFinding, setEditedFinding] = useState(null);
  const [showSensitiveData, setShowSensitiveData] = useState({});
  
  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(420);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);

  // Finding types configuration
  const FINDING_TYPES = [
    { name: 'Stealer Log', icon: Key, color: 'red' },
    { name: 'PII Exposure', icon: FileText, color: 'orange' },
    { name: 'Dark Web Mention', icon: MessageSquare, color: 'purple' }
  ];

  const STATUS_OPTIONS = [
    'Active',
    'Monitoring',
    'Remediated',
    'Resolved',
    'False Positive'
  ];

  const RISK_LEVELS = [
    'Critical',
    'High',
    'Medium',
    'Low',
    'Informational'
  ];

  // Get type config
  const getTypeConfig = (typeName) => {
    return FINDING_TYPES.find(t => t.name === typeName) || { name: typeName, icon: Database, color: 'gray' };
  };

  // Fetch dark web findings
  useEffect(() => {
    if (!isSignedIn || !activeCompany) return;
    
    const fetchFindings = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await fetch(`/api/companies/${activeCompany.id}/dark-web`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!res.ok) throw new Error(`Failed to load dark web findings (${res.status})`);
        
        const data = await res.json();
        setFindings(Array.isArray(data) ? data : []);
        
        if (data.length > 0 && !selectedFinding) {
          setSelectedFinding(data[0]);
        }
      } catch (e) {
        console.error('Dark web findings load error:', e);
        setError(e.message || 'Failed to load dark web findings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFindings();
  }, [isSignedIn, activeCompany, getToken]);

  // Handle mouse down on resizer
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      
      const minWidth = 280;
      const maxWidth = containerRect.width * 0.7;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Filter findings
  const filteredFindings = findings.filter(finding => {
    const matchesSearch = finding.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         finding.finding_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         finding.source?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         finding.stealer_username?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || finding.type === filterType;
    const matchesStatus = filterStatus === 'all' || finding.status === filterStatus;
    const matchesRisk = filterRisk === 'all' || finding.risk === filterRisk;
    return matchesSearch && matchesType && matchesStatus && matchesRisk;
  });

  // Get risk color
  const getRiskColor = (risk) => {
    const colors = {
      'Critical': 'bg-red-500/20 text-red-400 border-red-500/30',
      'High': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Medium': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Low': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Informational': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[risk] || colors['Informational'];
  };

  // Get status color
  const getStatusColor = (status) => {
    if (status === 'Active') return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (status === 'Monitoring') return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    if (status === 'Remediated') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status === 'Resolved') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status === 'False Positive') return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // Toggle sensitive data visibility
  const toggleSensitiveData = (field) => {
    setShowSensitiveData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Mask sensitive data
  const maskData = (data) => {
    if (!data) return '-';
    return '•'.repeat(Math.min(data.length, 20));
  };

  // Handle edit
  const handleEdit = () => {
    setEditedFinding({ ...selectedFinding });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedFinding(null);
    setIsEditing(false);
  };

  // Save changes
  const handleSave = async () => {
    if (!editedFinding || !activeCompany) {
      alert('No finding selected or company context missing.');
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(
        `/api/companies/${activeCompany.id}/dark-web/${editedFinding.finding_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editedFinding)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Failed to update finding (HTTP ${response.status})`);
      }

      const updated = await response.json();

      // Update local state
      setFindings(findings.map(f => 
        f.finding_id === updated.finding_id ? updated : f
      ));
      setSelectedFinding(updated);
      setIsEditing(false);
      setEditedFinding(null);
      alert(`Finding "${updated.finding_id}" updated successfully!`);
    } catch (error) {
      console.error('Error saving finding:', error);
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <div ref={containerRef} className="h-full flex relative">
      {/* LEFT SIDEBAR - Findings List */}
      <div 
        className="border-r border-slate-800/50 bg-slate-900/30 flex flex-col"
        style={{ width: `${leftPanelWidth}px`, minWidth: '280px', maxWidth: '70%' }}
      >
        <div className="px-4 py-4 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <Database className="w-5 h-5 text-cyan-400" />
              Dark Web Monitoring
            </h2>
            {canSeeSuperAdminSettings && (
              <button
                onClick={() => setShowAddDarkWebFinding(true)}
                className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-xs text-white font-medium transition-all"
              >
                + New
              </button>
            )}
          </div>

          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search findings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Types</option>
              {FINDING_TYPES.map(type => (
                <option key={type.name} value={type.name}>{type.name}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Status</option>
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <select
              value={filterRisk}
              onChange={(e) => setFilterRisk(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Risk Levels</option>
              {RISK_LEVELS.map(risk => (
                <option key={risk} value={risk}>{risk}</option>
              ))}
            </select>
          </div>

          <div className="mt-3 text-xs text-slate-400">
            {filteredFindings.length} finding{filteredFindings.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!activeCompany && (
            <div className="p-4 text-sm text-slate-400">No company selected.</div>
          )}
          {error && (
            <div className="m-4 bg-red-500/10 border border-red-500/30 rounded p-3 text-sm text-red-300">
              {error}
            </div>
          )}
          {loading && (
            <div className="p-4 text-sm text-slate-400">Loading findings...</div>
          )}
          {!loading && filteredFindings.length === 0 && (
            <div className="p-4 text-sm text-slate-500">No findings found.</div>
          )}
          {!loading && filteredFindings.map((finding) => {
            const isSelected = selectedFinding?.finding_id === finding.finding_id;
            const typeConfig = getTypeConfig(finding.type);
            const Icon = typeConfig.icon;
            
            return (
              <button
                key={finding.finding_id}
                onClick={() => {
                  setSelectedFinding(finding);
                  setIsEditing(false);
                  setEditedFinding(null);
                  setShowSensitiveData({});
                }}
                className={`w-full p-4 border-b border-slate-800/30 text-left transition-all ${
                  isSelected
                    ? 'bg-cyan-500/10 border-l-4 border-l-cyan-500'
                    : 'hover:bg-slate-800/30 border-l-4 border-l-transparent'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-cyan-400" />
                    <span className="text-xs text-slate-400">{finding.type}</span>
                  </div>
                  <span className="text-xs text-slate-500">{finding.discovered_date ? new Date(finding.discovered_date).toLocaleDateString() : '-'}</span>
                </div>

                <div className="text-sm font-medium text-slate-200 mb-2 line-clamp-2">
                  {finding.title}
                </div>

                <div className="text-xs text-slate-500 mb-2">
                  {finding.finding_id} {finding.source && `• ${finding.source}`}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(finding.status)}`}>
                    {finding.status}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getRiskColor(finding.risk)}`}>
                    {finding.risk}
                  </span>
                  {finding.severity_score && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border border-slate-600/30 bg-slate-800/30 text-slate-300">
                      Score: {finding.severity_score}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* RESIZABLE SEPARATOR */}
      <div
        className={`w-1 bg-slate-800/50 hover:bg-cyan-500/50 cursor-col-resize flex items-center justify-center transition-colors relative group ${
          isResizing ? 'bg-cyan-500/70' : ''
        }`}
        onMouseDown={handleMouseDown}
      >
        <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center">
          <GripVertical className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors" />
        </div>
      </div>

      {/* RIGHT PANEL - Finding Details */}
      <div className="flex-1 overflow-y-auto">
        {selectedFinding ? (
          <div className="p-8">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {React.createElement(getTypeConfig(selectedFinding.type).icon, {
                      className: "w-6 h-6 text-cyan-400"
                    })}
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedFinding.title}
                        onChange={(e) => setEditedFinding({ ...editedFinding, title: e.target.value })}
                        className="text-2xl font-bold bg-slate-800/50 border border-slate-700/50 rounded px-3 py-1 text-slate-100 flex-1"
                      />
                    ) : (
                      <h2 className="text-2xl font-bold text-slate-100">
                        {selectedFinding.title}
                      </h2>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-400">
                    <span className="font-mono">{selectedFinding.finding_id}</span>
                    <span>|</span>
                    {isEditing ? (
                      <select
                        value={editedFinding.type}
                        onChange={(e) => setEditedFinding({ ...editedFinding, type: e.target.value })}
                        className="bg-slate-800/50 border border-slate-700/50 rounded px-2 py-1 text-slate-200 text-sm"
                      >
                        {FINDING_TYPES.map(type => (
                          <option key={type.name} value={type.name}>{type.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span>{selectedFinding.type}</span>
                    )}
                    <span>|</span>
                    <span>{selectedFinding.discovered_date ? new Date(selectedFinding.discovered_date).toLocaleDateString() : '-'}</span>
                  </div>
                </div>
                {!isEditing && (
                  <button
                    onClick={() => setSelectedFinding(null)}
                    className="p-2 hover:bg-slate-800/50 rounded-lg transition-all"
                  >
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                )}
              </div>

              {/* Status Badges */}
              <div className="flex items-center gap-2 mb-4">
                {isEditing ? (
                  <>
                    <select
                      value={editedFinding.status}
                      onChange={(e) => setEditedFinding({ ...editedFinding, status: e.target.value })}
                      className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                    <select
                      value={editedFinding.risk}
                      onChange={(e) => setEditedFinding({ ...editedFinding, risk: e.target.value })}
                      className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                    >
                      {RISK_LEVELS.map(risk => (
                        <option key={risk} value={risk}>{risk}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(selectedFinding.status)}`}>
                      {selectedFinding.status}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getRiskColor(selectedFinding.risk)}`}>
                      {selectedFinding.risk} Risk
                    </span>
                    {selectedFinding.severity_score && (
                      <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border border-purple-500/30 bg-purple-500/10 text-purple-400">
                        Severity: {selectedFinding.severity_score}/100
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Warning for Critical/High Risk */}
              {(selectedFinding.risk === 'Critical' || selectedFinding.risk === 'High') && !isEditing && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-300">
                    <strong>High Risk Finding:</strong> Immediate attention required. Review remediation actions below.
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              {/* Source Information */}
              <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Source Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Source</div>
                    <div className="text-sm text-slate-200">{selectedFinding.source || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Source URL</div>
                    {selectedFinding.source_url ? (
                      <a
                        href={selectedFinding.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View Source
                      </a>
                    ) : (
                      <div className="text-sm text-slate-500">-</div>
                    )}
                  </div>
                </div>
              </div>

              {/* STEALER LOG SPECIFIC FIELDS */}
              {selectedFinding.type === 'Stealer Log' && (
                <>
                  <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                    <h3 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Compromised Credentials
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Target URL</div>
                        <div className="text-sm text-slate-200 font-mono">{selectedFinding.stealer_url || '-'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Username</div>
                          <div className="text-sm text-slate-200 font-mono">{selectedFinding.stealer_username || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1 flex items-center justify-between">
                            <span>Password</span>
                            <button
                              onClick={() => toggleSensitiveData('password')}
                              className="text-cyan-400 hover:text-cyan-300 transition-colors"
                            >
                              {showSensitiveData.password ? (
                                <EyeOff className="w-3 h-3" />
                              ) : (
                                <Eye className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                          <div className="text-sm text-red-400 font-mono">
                            {showSensitiveData.password ? selectedFinding.stealer_password : maskData(selectedFinding.stealer_password)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                      Infection Details
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Malware Family:</span>
                        <span className="ml-2 text-slate-200">{selectedFinding.malware_family || '-'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Infection Date:</span>
                        <span className="ml-2 text-slate-200">
                          {selectedFinding.infection_date ? new Date(selectedFinding.infection_date).toLocaleDateString() : '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Victim OS:</span>
                        <span className="ml-2 text-slate-200">{selectedFinding.victim_os || '-'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Victim IP:</span>
                        <span className="ml-2 text-slate-200 font-mono">{selectedFinding.victim_ip || '-'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Country:</span>
                        <span className="ml-2 text-slate-200">{selectedFinding.victim_country || '-'}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-slate-500">Malware Path:</span>
                        <div className="ml-2 text-slate-200 font-mono text-xs break-all">{selectedFinding.malware_path || '-'}</div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* PII EXPOSURE SPECIFIC FIELDS */}
              {selectedFinding.type === 'PII Exposure' && (
                <>
                  <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-4">
                    <h3 className="text-xs font-semibold text-orange-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Data Breach Details
                    </h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Breach Name</div>
                          <div className="text-sm text-slate-200">{selectedFinding.breach_name || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Breach Date</div>
                          <div className="text-sm text-slate-200">{selectedFinding.breach_date ? new Date(selectedFinding.breach_date).toLocaleDateString() : '-'}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Exposed Records</div>
                          <div className="text-sm text-orange-400 font-bold">
                            {selectedFinding.exposed_records ? selectedFinding.exposed_records.toLocaleString() : '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Data Types</div>
                          <div className="text-sm text-slate-200">
                            {selectedFinding.data_types && Array.isArray(selectedFinding.data_types) 
                              ? selectedFinding.data_types.join(', ')
                              : '-'
                            }
                          </div>
                        </div>
                      </div>
                      {selectedFinding.affected_users && (
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Affected Users</div>
                          <div className="text-sm text-slate-200 max-h-32 overflow-y-auto">
                            {selectedFinding.affected_users}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* DARK WEB MENTION SPECIFIC FIELDS */}
              {selectedFinding.type === 'Dark Web Mention' && (
                <>
                  <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                    <h3 className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Dark Web Chatter
                    </h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Platform</div>
                          <div className="text-sm text-slate-200">{selectedFinding.platform || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Author</div>
                          <div className="text-sm text-slate-200 font-mono">{selectedFinding.author_handle || '-'}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Mention Type</div>
                          <div className="text-sm text-slate-200">{selectedFinding.mention_type || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Post Date</div>
                          <div className="text-sm text-slate-200">
                            {selectedFinding.post_date ? new Date(selectedFinding.post_date).toLocaleDateString() : '-'}
                          </div>
                        </div>
                      </div>
                      {selectedFinding.post_content && (
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Post Content</div>
                          <div className="text-sm text-slate-200 p-3 bg-slate-900/50 rounded border border-slate-700/30 max-h-48 overflow-y-auto whitespace-pre-wrap">
                            {selectedFinding.post_content}
                          </div>
                        </div>
                      )}
                      {selectedFinding.threat_indicators && (
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Threat Indicators</div>
                          <div className="text-sm text-orange-400">{selectedFinding.threat_indicators}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Remediation Actions */}
              {(selectedFinding.remediation_actions || selectedFinding.remediation_date || isEditing) && (
                <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Remediation
                  </h3>
                  <div className="space-y-3">
                    {selectedFinding.remediation_date && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Remediation Date</div>
                        <div className="text-sm text-slate-200">{selectedFinding.remediation_date}</div>
                      </div>
                    )}
                    {selectedFinding.remediation_actions && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Actions Taken</div>
                        <div className="text-sm text-slate-200 whitespace-pre-wrap">
                          {selectedFinding.remediation_actions}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {(selectedFinding.notes || isEditing) && (
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                    Notes
                  </h3>
                  {isEditing ? (
                    <textarea
                      rows={4}
                      value={editedFinding.notes || ''}
                      onChange={(e) => setEditedFinding({ ...editedFinding, notes: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="Additional analysis and notes..."
                    />
                  ) : (
                    <p className="text-slate-200 whitespace-pre-wrap">{selectedFinding.notes}</p>
                  )}
                </div>
              )}

              {/* Metadata */}
              <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Metadata
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Created:</span>
                    <span className="ml-2 text-slate-200">
                      {selectedFinding.created_at ? new Date(selectedFinding.created_at).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Created By:</span>
                    <span className="ml-2 text-slate-200">{selectedFinding.created_by || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Updated:</span>
                    <span className="ml-2 text-slate-200">
                      {selectedFinding.updated_at ? new Date(selectedFinding.updated_at).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Updated By:</span>
                    <span className="ml-2 text-slate-200">{selectedFinding.updated_by || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {canEditIncidents && (
                <div className="flex gap-3">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-sm text-white transition-all flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleEdit}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Finding
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Database className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-400">Select a finding to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DarkWebView;